/**
 * Utilities for detecting, parsing, and formatting rate limit (HTTP 429 / Too Many Requests) errors.
 */

export function parseRateLimitError(error) {
  if (!error) return null;

  const status = error.status || error.statusCode || error.response?.status;
  const data = error.response?.data;
  
  // Extract raw message from diverse error shapes (Smarty array, DRF object, Axios, Error instance)
  let rawMsg = '';
  if (typeof error === 'string') {
    rawMsg = error;
  } else if (Array.isArray(data) && data[0]?.message) {
    rawMsg = String(data[0].message);
  } else if (typeof data?.detail === 'string') {
    rawMsg = data.detail;
  } else if (typeof data?.message === 'string') {
    rawMsg = data.message;
  } else if (typeof data?.error === 'string') {
    rawMsg = data.error;
  } else if (error.message) {
    rawMsg = String(error.message);
  }

  const is429 = status === 429;
  const hasRateLimitKeyword = /too many requests|rate limit|throttled|quota exceeded|address_rate_limited/i.test(rawMsg);
  const errorCode = error.code || data?.code;
  if (errorCode === 'address_rate_limited') {
    return rawMsg || 'Too many address lookups. Please wait and try again.';
  }

  if (!is429 && !hasRateLimitKeyword && !error.isRateLimit) {
    return null;
  }

  // Extract duration if present
  let seconds = null;

  // Check headers: retry-after
  const headers = error.response?.headers || error.headers || {};
  const retryAfter = headers['retry-after'] || headers['Retry-After'];
  if (retryAfter) {
    const parsed = parseInt(retryAfter, 10);
    if (!isNaN(parsed) && parsed > 0) {
      seconds = parsed;
    }
  }

  // Check in message strings (e.g., "Expected available in 58 seconds.", "wait 60 seconds", etc.)
  if (!seconds && rawMsg) {
    const match = rawMsg.match(/(?:available in|wait|retry in|in)\s*(\d+)\s*(?:seconds|second|secs|sec|s\b)/i);
    if (match) {
      const parsed = parseInt(match[1], 10);
      if (!isNaN(parsed) && parsed > 0) {
        seconds = parsed;
      }
    }
  }

  // If DRF throttled message is explicit and already has formatted duration, use it
  if (typeof data?.detail === 'string' && /throttled/i.test(data.detail)) {
    return data.detail;
  }

  if (seconds && seconds > 0) {
    if (seconds >= 60) {
      const minutes = Math.ceil(seconds / 60);
      return `Too many requests. Please wait ${minutes} minute${minutes === 1 ? '' : 's'} and try again.`;
    }
    return `Too many requests. Please wait ${seconds} second${seconds === 1 ? '' : 's'} and try again.`;
  }

  return 'Too many requests. Please wait a moment and try again.';
}
